import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";
import {
  Plus, Edit, Trash2, Loader2, Image as ImageIcon,
  Bold, Italic, Strikethrough, List, ListOrdered, Link as LinkIcon, Highlighter, Undo, Redo,
} from "lucide-react";
import { API } from "@/lib/admin-constants";

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-cfc-border bg-cfc-cream">
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive("bold") ? "bg-cfc-primary text-white" : ""}>
        <Bold size={16} />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive("italic") ? "bg-cfc-primary text-white" : ""}>
        <Italic size={16} />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive("strike") ? "bg-cfc-primary text-white" : ""}>
        <Strikethrough size={16} />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHighlight().run()} className={editor.isActive("highlight") ? "bg-cfc-primary text-white" : ""}>
        <Highlighter size={16} />
      </Button>
      <div className="w-px h-6 bg-cfc-border mx-1" />
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive("bulletList") ? "bg-cfc-primary text-white" : ""}>
        <List size={16} />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive("orderedList") ? "bg-cfc-primary text-white" : ""}>
        <ListOrdered size={16} />
      </Button>
      <div className="w-px h-6 bg-cfc-border mx-1" />
      <Button type="button" variant="ghost" size="sm" onClick={setLink} className={editor.isActive("link") ? "bg-cfc-primary text-white" : ""}>
        <LinkIcon size={16} />
      </Button>
      <div className="w-px h-6 bg-cfc-border mx-1" />
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
        <Undo size={16} />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
        <Redo size={16} />
      </Button>
    </div>
  );
};

const ContentManagement = () => {
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    content_html: "",
    image_id: null,
    image_fit: "cover",
    link_url: "",
    is_published: true,
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({ openOnClick: false }),
      Highlight,
    ],
    content: formData.content_html,
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({ ...prev, content_html: editor.getHTML() }));
    },
  });

  // Sync editor when switching between posts
  useEffect(() => {
    if (editor && formData.content_html !== editor.getHTML()) {
      editor.commands.setContent(formData.content_html);
    }
  }, [editor, editingPost]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/news`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title,
        content: post.content,
        content_html: post.content_html || post.content || "",
        image_id: post.image_id || null,
        image_fit: post.image_fit || "cover",
        link_url: post.link_url || "",
        is_published: post.is_published,
      });
      setImagePreview(post.image_id ? `${API}/files/${post.image_id}` : null);
      if (editor) editor.commands.setContent(post.content_html || post.content || "");
    } else {
      setEditingPost(null);
      setFormData({ title: "", content: "", content_html: "", image_id: null, image_fit: "cover", link_url: "", is_published: true });
      setImagePreview(null);
      if (editor) editor.commands.setContent("");
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    const plainText = formData.content_html.replace(/<[^>]*>/g, "").trim();
    const submitData = { ...formData, content: plainText || formData.content };
    try {
      if (editingPost) {
        await axios.put(`${API}/news/${editingPost.post_id}`, submitData, { withCredentials: true });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/news`, submitData, { withCredentials: true });
        toast.success("Post created");
      }
      setIsDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/news/${postId}`, { withCredentials: true });
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, GIF, or WebP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, image_id: response.data.file_id }));
      setImagePreview(URL.createObjectURL(file));
      toast.success("Image uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image_id: null }));
    setImagePreview(null);
  };

  return (
    <div data-testid="content-management">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold text-cfc-primary">News & Content</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary" data-testid="new-post-btn">
              <Plus size={18} className="mr-2" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-cfc-primary">
                {editingPost ? "Edit Post" : "New Post"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="post-title-input" />
              </div>
              <div>
                <Label>Featured Image</Label>
                <div className="mt-2">
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img src={imagePreview} alt="Preview" className="max-h-48 rounded border border-cfc-border" />
                      <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={removeImage}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-cfc-border rounded cursor-pointer hover:border-cfc-primary transition-colors">
                      <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                      {uploading ? (
                        <Loader2 className="w-8 h-8 text-cfc-text-muted animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="w-8 h-8 text-cfc-text-muted mb-2" />
                          <span className="text-sm text-cfc-text-muted">Click to upload image</span>
                          <span className="text-xs text-cfc-text-muted mt-1">JPEG, PNG, GIF, WebP (max 5MB)</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>
              {(imagePreview || formData.image_id) && (
                <div>
                  <Label>Image Fit</Label>
                  <Select value={formData.image_fit || "cover"} onValueChange={(v) => setFormData({ ...formData, image_fit: v })}>
                    <SelectTrigger className="mt-1" data-testid="post-image-fit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cover">Cover (fill & crop)</SelectItem>
                      <SelectItem value="contain">Contain (show full image)</SelectItem>
                      <SelectItem value="auto">Auto (natural size)</SelectItem>
                      <SelectItem value="fill">Stretch to fill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Content</Label>
                <div className="mt-2 border border-cfc-border rounded overflow-hidden">
                  <EditorToolbar editor={editor} />
                  <EditorContent editor={editor} className="prose prose-sm max-w-none p-4 min-h-[150px] focus:outline-none bg-white" />
                </div>
              </div>
              <div>
                <Label htmlFor="link_url">External Link (optional)</Label>
                <Input id="link_url" type="url" value={formData.link_url} onChange={(e) => setFormData({ ...formData, link_url: e.target.value })} placeholder="https://..." data-testid="post-link-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="is_published" checked={formData.is_published} onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })} data-testid="post-published-switch" />
                <Label htmlFor="is_published">Published</Label>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-post-btn">
                {editingPost ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-cfc-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((post) => (
              <TableRow key={post.post_id} data-testid={`post-row-${post.post_id}`}>
                <TableCell>
                  {post.image_id ? (
                    <img src={`${API}/files/${post.image_id}`} alt="" className="w-12 h-12 object-cover rounded" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-12 h-12 bg-cfc-cream rounded flex items-center justify-center">
                      <ImageIcon size={16} className="text-cfc-text-muted" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{post.title}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    post.is_published ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                  }`}>
                    {post.is_published ? "Published" : "Draft"}
                  </span>
                </TableCell>
                <TableCell>{new Date(post.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(post)} data-testid={`edit-post-${post.post_id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(post.post_id)} className="text-cfc-destructive hover:text-cfc-destructive" data-testid={`delete-post-${post.post_id}`}>
                    <Trash2 size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {posts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-cfc-text-muted py-8">
                  No posts yet. Create your first news post.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default ContentManagement;
